import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Drawer, IconButton } from "@mui/material";
import dayjs from "dayjs";
import { Settings } from "@mui/icons-material";

import "./styles.css";
import { DASHBOARD_ITEM_TYPE } from "./constants";
import { EngineTrendWidget } from "./widgets/engineTrend";
import { ElectricTrendWidget } from "./widgets/electricTrend";
import { SepTrendWidget } from "./widgets/sepTrend";
import { CustomTrendWidget } from "./widgets/customTrend";
import Value from "./widgets/value";
import { AdditionalElectricTrendWidget } from "./widgets/additionalElectricTrend";
import { LiveTrendWidget } from "./widgets/liveTrend";
import { ComponentSettings } from "./settings/component/componentSettings";
import { postDashboard } from "../../api/dashboard";
import { VESSEL_TYPE } from "../../constants/constants";

const SETTING_BUTTON_SIZE = 24;

const Display = React.memo((props) => {
  const { vessel, item, index, startDate, endDate, setChartRef } = props;
  const type = item.type;
  const chNos = item.chNos;
  switch (type) {
    case DASHBOARD_ITEM_TYPE.VALUE:
      return <Value chNo={chNos?.[0] ?? ""} />;
    case DASHBOARD_ITEM_TYPE.CUSTOM:
      return (
        <CustomTrendWidget
          vessel={vessel}
          chartId={item.chartId}
          index={index}
          startDate={startDate}
          endDate={endDate}
        />
      );
    case DASHBOARD_ITEM_TYPE.ENGINE:
      return (
        <EngineTrendWidget
          vessel={vessel}
          selectedChart={item.chartType}
          enginePosition={item.enginePosition}
          index={index}
          startDate={startDate}
          endDate={endDate}
        />
      );
    case DASHBOARD_ITEM_TYPE.ELECTRIC:
      return (
        <ElectricTrendWidget
          vessel={vessel}
          selectedChart={item.chartType}
          index={index}
          startDate={startDate}
          endDate={endDate}
        />
      );
    case DASHBOARD_ITEM_TYPE.SEP:
      return (
        <SepTrendWidget
          vessel={vessel}
          selectedChart={item.chartType}
          index={index}
          startDate={startDate}
          endDate={endDate}
        />
      );
    case DASHBOARD_ITEM_TYPE.ADDITIONAL_ELECTRIC:
      return (
        <AdditionalElectricTrendWidget
          vessel={vessel}
          selectedChart={item.chartType}
          index={index}
          startDate={startDate}
          endDate={endDate}
        />
      );
    case DASHBOARD_ITEM_TYPE.LIVE:
      return (
        <LiveTrendWidget 
          vessel={vessel} 
          item={item} 
          index={index} 
          setChartRef={setChartRef}
        />
      );
  }
  return <></>;
});

Display.propTypes = {
  vessel: PropTypes.object,
  item: PropTypes.object,
  index: PropTypes.number,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  setChartRef: PropTypes.func,
};

export const DashboardCard = (props) => {
  const { vessel, item, index, isEdit, dashboard } = props;
  const isSepCharts = vessel.vesselType === VESSEL_TYPE.SEP && item.type === DASHBOARD_ITEM_TYPE.SEP;
  const [startDate, setStartDate] = useState(dayjs.utc().subtract(isSepCharts ? 1 : 7, "d").startOf("m"));
  const [endDate, setEndDate] = useState(dayjs.utc().startOf("m"));
  const [chartRef, setChartRef] = useState();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [thickness, setThickness] = useState(item.thickness ?? 2);

  const handleDrawerClose = () => {
    setIsDrawerOpen((prev) => !prev);
  }

  useEffect(() => {
    if (!dashboard) return;

    if (item.type === "liveTrend" && thickness !== item.thickness) {
      const saveDashboard = async (vesselId, dashboard) => {
        await postDashboard(vesselId, dashboard);;
      }
      dashboard.items.find(i => i.id === item.id).thickness = thickness;
      saveDashboard(vessel.id, dashboard);
    }
  }, [thickness]);

  return (
    <Box sx={{ background: "#36485a", position: "relative", overflow: "hidden", borderRadius: "4px", height: "100%", padding: "8px" }}>
      {item?.type !== DASHBOARD_ITEM_TYPE.VALUE &&
        !isEdit && (
          <IconButton
            onClick={() => {
              handleDrawerClose();
            }}
            size="small"
            sx={{
              color: "white",
              position: "absolute",
              left: `calc(100% - ${SETTING_BUTTON_SIZE + 8}px)`,
              top: "8px",
              height: `${SETTING_BUTTON_SIZE}px`,
              width: `${SETTING_BUTTON_SIZE}px`,
            }}
          >
            <Settings />
          </IconButton>
        )}

      <Drawer
        anchor="right"
        variant="persistent"
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{
          style: { position: 'absolute' } // Restricts the modal container
        }}
        slotProps={{
          paper: { sx: { position: 'absolute' } }, // Restricts the drawer panel
          backdrop: { sx: { position: 'absolute' } } // Restricts the background overlay
        }}
        sx={{
          width: "100%",
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "calc(100% - 50px)",
            marginTop: "50px", //for top bar
            padding: "8px",
            background: "rgba(37, 45, 57, 0.9)",
            boxSizing: "border-box",
          },
        }}
      >
        <ComponentSettings
          vessel={vessel}
          item={item}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          chartRef={chartRef}
          thickness={thickness}
          setThickness={setThickness}
        />
      </Drawer>
      <Box open={isDrawerOpen} sx={{ height: "100%" }}>
        <Display
          vessel={vessel}
          item={item}
          index={index}
          startDate={startDate}
          endDate={endDate}
          setChartRef={setChartRef}
          thickness={thickness}
        />
      </Box>
    </Box>
  );
};

DashboardCard.propTypes = {
  vessel: PropTypes.object,
  dashboard: PropTypes.object,
  item: PropTypes.object,
  index: PropTypes.number,
  isEdit: PropTypes.bool,
};
